import {
  Shield,
  BadgeCheck,
  FileText,
  CheckCircle2,
  ClipboardCheck,
  Layers,
  Wind,
} from "lucide-react";
import { AreaPageShell } from "./AreaPageShell";

export interface InspectionFaq {
  question: string;
  answer: string;
}

export interface InspectionPageData {
  city: string;
  citySlug: string;
  serviceName: string;
  title: string;
  description: string;
  h1: string;
  heroIntro: string;
  climateText: string;
  localPresenceText: string;
  reportText: string;
  pricingText: string;
  includedText: string;
  insuranceText: string;
  step1: string;
  step2: string;
  step3: string;
  step4: string;
  exteriorText: string;
  structuralText: string;
  ventilationText: string;
  gutterText: string;
  faqs: InspectionFaq[];
}

const buildSchema = (data: InspectionPageData) => {
  const base = "https://www.saskroofingcompany.com";
  const url = `${base}/areas/${data.citySlug}/inspection`;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": `${base}/#localbusiness`,
    name: "Sask Roofing Company",
    url: `${base}/`,
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: data.city,
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    serviceType: "Roof Inspection",
    name: "Roof Inspection",
    description: data.description,
    url,
    provider: {
      "@id": `${base}/#localbusiness`,
      "@type": "RoofingContractor",
      name: "Sask Roofing Company",
      url: `${base}/`,
      telephone: "+1-639-739-7969",
    },
    areaServed: { "@type": "City", name: data.city },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "CAD",
      lowPrice: "0",
      highPrice: "0",
    },
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: `${base}/contact`,
      servicePhone: {
        "@type": "ContactPoint",
        telephone: "+1-639-739-7969",
        contactType: "customer service",
        areaServed: "CA-SK",
        availableLanguage: "English",
      },
    },
    termsOfService: `${base}/about#warranty`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "87",
      bestRating: "5",
      worstRating: "1",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${base}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: `${base}/areas`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: data.city,
        item: `${base}/areas/${data.citySlug}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Roof Inspection",
        item: url,
      },
    ],
  };

  return [breadcrumbSchema, localBusinessSchema, serviceSchema, faqSchema];
};

export function InspectionPage({ data }: { data: InspectionPageData }) {
  return (
    <AreaPageShell
      data={{
        ...data,
        schema: buildSchema(data),
        canonical: `/areas/${data.citySlug}/inspection`,
      }}
      heroImage="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/67bfca73-3cfe-4fd0-b9fd-9ed4f4780799.png&output=webp&q=60"
      heroAlt={`Roof inspection in ${data.city}`}
      whyBlocks={[
        {
          icon: Wind,
          title: `What Saskatchewan weather does to ${data.city} roofs`,
          text: data.climateText,
        },
        {
          icon: Shield,
          title: `Local roof inspection presence in ${data.city}`,
          text: data.localPresenceText,
        },
        {
          icon: FileText,
          title: `Roof inspection reports & quotes in ${data.city}`,
          text: data.reportText,
        },
      ]}
      costBlocks={[
        {
          title: `Roof inspection pricing in ${data.city} (FREE)`,
          text: data.pricingText,
        },
        {
          title: `What's included at no cost in ${data.city}`,
          text: data.includedText,
        },
        {
          title: `Insurance & inspection documentation in ${data.city}`,
          text: data.insuranceText,
        },
      ]}
      extraCostTiles={[
        {
          label: "Inspection",
          value: "FREE",
          note: "no-obligation",
        },
        {
          label: "Diagnosis",
          value: "Same-day",
          note: "while we're on site",
        },
        {
          label: "Written quote",
          value: "Included",
          note: "fixed-price",
        },
      ]}
      extraCallout={{
        icon: CheckCircle2,
        title: `No-obligation roof inspection in ${data.city}`,
        text: "Every inspection is free and no-obligation. We tell you exactly what your roof needs and nothing it doesn't. If a repair makes more sense than a replacement, that's what we'll recommend.",
      }}
      processSteps={[
        {
          title: `Scheduling your free roof inspection in ${data.city}`,
          text: data.step1,
        },
        {
          title: `On-site visual & structural roof assessment in ${data.city}`,
          text: data.step2,
        },
        {
          title: `Same-day roof diagnosis & quote in ${data.city}`,
          text: data.step3,
        },
        {
          title: `Written roof inspection report & follow-up`,
          text: data.step4,
        },
      ]}
      materialBlocks={[
        {
          icon: ClipboardCheck,
          title: `Exterior roof surface assessment in ${data.city}`,
          text: data.exteriorText,
        },
        {
          icon: Shield,
          title: `Structural & decking check in ${data.city}`,
          text: data.structuralText,
        },
        {
          icon: Wind,
          title: `Ventilation & insulation review in ${data.city}`,
          text: data.ventilationText,
        },
        {
          icon: Layers,
          title: `Gutter & drainage inspection in ${data.city}`,
          text: data.gutterText,
        },
      ].slice(0, 3)}
    />
  );
}
